// ============================================
// Phase 6: 数据绑定类型定义
// ============================================

import type {
  DatasourceConfig,
  DataBinding,
  MqttConfig,
  RestConfig,
  WebSocketConfig,
  StaticConfig,
} from './designer';

// ============================================
// 运行时数据源状态
// ============================================

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface RuntimeDatasource {
  id: string;
  config: DatasourceConfig;
  status: ConnectionStatus;
  lastUpdate: number;
  lastError?: string;
  dataCount: number;
}

// ============================================
// 数据适配器接口
// ============================================

export interface DatasourceAdapter {
  readonly type: DatasourceConfig['type'];
  connect(config: DatasourceConfig): Promise<void>;
  disconnect(): void;
  subscribe(topic: string, callback: (data: any) => void): void;
  unsubscribe(topic: string): void;
  publish?(topic: string, data: any): void;
  getStatus(): ConnectionStatus;
}

// ============================================
// 绑定表达式类型
// ============================================

export type ExpressionType =
  | 'direct'      // 直接映射: data.value
  | 'template'    // 模板表达式: {{data.value * 2}}
  | 'condition'   // 条件表达式: data.value > 100 ? 'red' : 'green'
  | 'aggregate';  // 聚合: avg(data.values)

export interface BindingExpression {
  type: ExpressionType;
  expression: string;
  fallback?: any;
}

// ============================================
// 属性路径定义
// ============================================

export interface PropertyPath {
  /** 节点属性的点分路径, 如 'config.color', 'transform.position.x' */
  path: string;
  /** 属性值类型 */
  valueType: 'number' | 'string' | 'boolean' | 'array' | 'object';
  /** 最小值 (number类型) */
  min?: number;
  /** 最大值 (number类型) */
  max?: number;
}

/** 预定义的可绑定属性 */
export const BINDABLE_PROPERTIES: PropertyPath[] = [
  // 变换
  { path: 'transform.position.x', valueType: 'number' },
  { path: 'transform.position.y', valueType: 'number' },
  { path: 'transform.position.z', valueType: 'number' },
  { path: 'transform.rotation.x', valueType: 'number' },
  { path: 'transform.rotation.y', valueType: 'number' },
  { path: 'transform.rotation.z', valueType: 'number' },
  { path: 'transform.scale.x', valueType: 'number', min: 0 },
  { path: 'transform.scale.y', valueType: 'number', min: 0 },
  { path: 'transform.scale.z', valueType: 'number', min: 0 },
  // 材质
  { path: 'config.color', valueType: 'string' },
  { path: 'config.metalness', valueType: 'number', min: 0, max: 1 },
  { path: 'config.roughness', valueType: 'number', min: 0, max: 1 },
  { path: 'config.opacity', valueType: 'number', min: 0, max: 1 },
  // 灯光
  { path: 'config.intensity', valueType: 'number', min: 0 },
  { path: 'config.lightType', valueType: 'string' },
  // 可见性
  { path: 'visible', valueType: 'boolean' },
];

// ============================================
// 数据转换函数
// ============================================

export type TransformFn = (value: any, data?: any) => any;

export const DATA_TRANSFORMS: Record<string, TransformFn> = {
  // 数值转换
  'number': (v) => Number(v) || 0,
  'clamp': (v, { min = 0, max = 1 } = {}) => Math.min(max, Math.max(min, Number(v) || 0)),
  'scale': (v, { factor = 1 } = {}) => (Number(v) || 0) * factor,
  'normalize': (v, { min = 0, max = 100 } = {}) => {
    const n = Number(v) || 0;
    return Math.min(1, Math.max(0, (n - min) / (max - min)));
  },
  // 颜色转换
  'tempToColor': (v) => {
    const t = Number(v) || 0;
    if (t < 20) return '#3498db';
    if (t < 30) return '#2ecc71';
    if (t < 40) return '#f39c12';
    return '#e74c3c';
  },
  'valueToColor': (v, { threshold = 0.5, lowColor = '#2ecc71', highColor = '#e74c3c' } = {}) => {
    return Number(v) > threshold ? highColor : lowColor;
  },
  // 状态映射
  'statusToLabel': (v) => {
    const statusMap: Record<number, string> = { 0: '离线', 1: '在线', 2: '告警', 3: '故障' };
    return statusMap[Number(v)] || '未知';
  },
  // 数学运算
  'abs': (v) => Math.abs(Number(v) || 0),
  'round': (v) => Math.round(Number(v) || 0),
  'floor': (v) => Math.floor(Number(v) || 0),
  'ceil': (v) => Math.ceil(Number(v) || 0),
  'modulo': (v, { divisor = 1 } = {}) => (Number(v) || 0) % divisor,
  // 字符串
  'toFixed': (v, { digits = 2 } = {}) => (Number(v) || 0).toFixed(digits),
  'concat': (v, { prefix = '', suffix = '' } = {}) => `${prefix}${v}${suffix}`,
};

// ============================================
// 绑定评估结果
// ============================================

export interface BindingEvaluationResult {
  bindingId: string;
  nodeId: string;
  property: string;
  value: any;
  success: boolean;
  error?: string;
  timestamp: number;
}
